import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import {
  Button,
  Col,
  Divider,
  Flex,
  Row,
  Select,
  Space,
  Spin,
  Tag
} from "antd";

import { ROUTES } from "@app/constants";
import { useAuth } from "@entities/auth";
import { BreifInfluencerV2StatusEnum } from "@entities/brief/lib/constants";
import { useGetBriefInfluencerIdFacets } from "@entities/brief/lib/hooks/useGetBriefInfluencerIdFacets";
import { useGetBriefsByInfId } from "@entities/brief/lib/hooks/useGetBriefsByInfId";
import useGetDeliverableConfigurators from "@entities/deliverableConfigurator/lib/hooks/useGetDeliverableConfigurators";
import { BriefTimelinePopOver } from "@features/briefTimeline/BriefTimelinePopOver";
import { IDeliverableConfigurator } from "@shared/lib/types";
import { formatData } from "@shared/lib/utils/formatData";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { BriefDeliverablePill } from "@widgets/CampaignBrief/ui/CampaignBriefTable/BriefDeliverablePill";
import { format } from "date-fns";
import { useState } from "react";
import styles from "../styles.module.scss";

interface Props {
  infId: string;
  socialMediaId?: string;
}

export function Campaigns({ infId, socialMediaId }: Props) {
  const [mainStatus, setMainStatus] = useState<string[]>([]);
  const [socialMediaObjectIds, setSocialMediaObjectIds] = useState<string[]>(
    []
  );
  const { deliverableConfiguratorsData } = useGetDeliverableConfigurators();
  const configurators = deliverableConfiguratorsData?.data?.data || [];

  const { beUser } = useAuth();

  const isSuperAdminPermission =
    beUser?.permissions?.includes("super") || false;

  const isViewBrandCostPermission =
    beUser?.permissions?.includes("view-brand-cost") ||
    isSuperAdminPermission ||
    false;
  const isViewInfluencerCostPermission = beUser?.demoMode
    ? false
    : beUser?.permissions?.includes("view-influencer-cost") ||
      isSuperAdminPermission ||
      false;

  const [brandIds, setBrandIds] = useState<string[]>([]);

  const { facets } = useGetBriefInfluencerIdFacets(infId, socialMediaId);

  const { briefInfluencers, isFetching, isError } = useGetBriefsByInfId({
    influencerId: infId,
    socialMediaObjectIds,
    socialMediaId,
    brandIds,
    mainStatus,
  });

  const briefInfluencersData = briefInfluencers?.data || [];

  return (
    <div className={styles.card}>
      <Space
        style={{
          justifyContent: "space-between",
          width: "100%",
          alignItems: "start",
          marginBottom: "12px",
        }}
      >
        <div className={styles.permTitle}>Campaigns</div>

        <Space>
          <Select
            mode="multiple"
            options={facets?.brands?.map((brand) => ({
              label: brand.brand.name,
              value: brand.brand._id,
            }))}
            placeholder="Select Brand"
            style={{ width: 250 }}
            onChange={(value) => setBrandIds(value)}
            value={brandIds}
          />
          <Select
            options={facets?.socialMedias?.map((socialMedia) => ({
              label: (
                <Space>
                  {getPlatformIcon(socialMedia.socialMedia.platform)}
                  {socialMedia.socialMedia.username}
                </Space>
              ),
              value: socialMedia.socialMedia._id,
            }))}
            placeholder="Select Social Media"
            style={{ width: 250 }}
            onChange={(value) => setSocialMediaObjectIds(value)}
            value={socialMediaObjectIds}
            mode="multiple"
          />
          <Select
            mode="multiple"
            value={mainStatus}
            onChange={(value) => setMainStatus(value)}
            options={[
              { label: "Brief", value: "Brief" },
              { label: "Campaign", value: "Campaign" },
            ]}
            placeholder="Select Status"
            style={{ width: 250 }}
          />
        </Space>
      </Space>

      <Divider style={{ margin: 0, marginBottom: 16 }} />

      {isFetching ? (
        <Row justify="center" align="middle">
          <Spin />
        </Row>
      ) : briefInfluencersData.length ? (
        <Flex vertical gap={16}>
          <Col span={24}>
            <Row style={{ width: "100%" }}>
              <Col span={6}>
                <div>Brief</div>
              </Col>
              <Col span={4}>
                <div>Deliverables</div>
              </Col>
              <Col span={2}>
                <div>Payment Term</div>
              </Col>

              {isViewBrandCostPermission ? (
                <Col span={3}>
                  <div>Brand Cost</div>
                </Col>
              ) : null}

              {isViewInfluencerCostPermission ? (
                <Col span={3}>
                  <div>Influencer Cost</div>
                </Col>
              ) : null}

              <Col span={3}>
                <div>Created At</div>
              </Col>

              <Col span={3}>
                <div>Main Status</div>
              </Col>
            </Row>
          </Col>
          <div style={{ maxHeight: "280px", overflowY: "auto" }}>
            {briefInfluencersData.map((permItem) => (
              <>
                <PermTile
                  briefInfluencer={permItem}
                  isViewBrandCostPermission={isViewBrandCostPermission}
                  isViewInfluencerCostPermission={
                    isViewInfluencerCostPermission
                  }
                  configurators={configurators}
                />
                <Divider style={{ margin: 10 }} />
              </>
            ))}
          </div>
        </Flex>
      ) : (
        <div>No Campaigns found</div>
      )}
    </div>
  );
}

export function PermTile({
  briefInfluencer,
  isViewBrandCostPermission,
  isViewInfluencerCostPermission,
  configurators,
}: {
  briefInfluencer: IBriefInfluencerV2;
  isViewBrandCostPermission: boolean;
  isViewInfluencerCostPermission: boolean;
  configurators: IDeliverableConfigurator[];
}) {
  const hasDeliverableInfo =
    briefInfluencer?.deliverableV2?.name ||
    briefInfluencer?.deliverableV2?.setNumber ||
    (briefInfluencer?.deliverableV2?.purchaseItems?.length ?? 0) >
      0 ||
    Boolean(briefInfluencer?.deliverableV2?.notes?.trim());

  const [deliverableModalProps, setDeliverableModalProps] = useState<{
    visible: boolean;
    deliverableId?: string;
    briefInfluencerIds: string[];
    type: "view" | "create";
  }>({
    visible: false,
    deliverableId: "",
    briefInfluencerIds: [],
    type: "create",
  });
  const handleClick = () => {
    window.open(
      `${ROUTES.BRIEF_V2.replace(
        ":briefId",
        briefInfluencer.briefId
      )}?influencerSearchQuery=${briefInfluencer.socialMedia.username}&status=${
        BreifInfluencerV2StatusEnum.All
      }`,
      "_blank"
    );
  };
  return (
    <>
      <Row
        style={{ width: "100%", cursor: "pointer" }}
        // justify={"space-between"}
        onClick={handleClick}
      >
        <Col span={6}>
          <div>
            <div className={styles.permItemTitle}>
              <TextWithTooltip
                text={briefInfluencer?.brief?.name}
                limit={50}
                textClassName={styles.permItemTitle}
              />
            </div>
            <TextWithTooltip
              text={briefInfluencer?.brand?.name}
              limit={20}
              textClassName={styles.permItemDesc}
            />
          </div>
        </Col>
        <Col span={4}>
          <Flex vertical gap={8} align="start">
            <Flex gap={8} align="flex-start" wrap="wrap">
              {hasDeliverableInfo && briefInfluencer.deliverableV2 ? (
                <BriefDeliverablePill
                  deliverable={briefInfluencer.deliverableV2}
                  configurators={configurators}
                />
              ) : (
                <span>N/A</span>
              )}
              <Button
                type="link"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setDeliverableModalProps({
                    visible: true,
                    deliverableId:
                      briefInfluencer?.deliverableV2?._id || "",
                    type: "view",
                    briefInfluencerIds: [briefInfluencer._id],
                  });
                }}
                // onClick={(e) => {
                //   e.stopPropagation();
                //   e.preventDefault();
                //   window.open(`${briefInfluencer?.socialMedia?.link}`, "_blank");
                // }}
              >
                View
              </Button>
            </Flex>

            <Button
              type="link"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                window.open(`${briefInfluencer?.socialMedia?.link}`, "_blank");
              }}
              style={{ padding: 0, margin: 0 }}
            >
              <Space size={2}>
                {getPlatformIcon(briefInfluencer.socialMedia.platform || "")}
                <TextWithTooltip
                  text={briefInfluencer.socialMedia.username}
                  limit={10}
                  // textClassName={styles.permItemDesc}
                />
              </Space>
            </Button>
          </Flex>
        </Col>
        <Col span={2}>
          <div>{briefInfluencer?.paymentTerms || "N/A"}</div>
        </Col>

        {isViewBrandCostPermission ? (
          <Col span={3}>
            <Space size={2}>
              <BriefTimelinePopOver
                briefInfluencer={briefInfluencer}
                type="brand"
              />
              <div className={styles.permItemDesc}>
                {briefInfluencer?.brandCost?.cost
                  ? formatData(briefInfluencer?.brandCost?.cost, "0,00.00")
                  : "N/A"}
              </div>
            </Space>
          </Col>
        ) : null}

        {isViewInfluencerCostPermission ? (
          <Col span={3}>
            <Space size={2}>
              <BriefTimelinePopOver
                briefInfluencer={briefInfluencer}
                type="influencer"
              />
              <div className={styles.permItemDesc}>
                {briefInfluencer?.influencerCost?.cost
                  ? formatData(briefInfluencer?.influencerCost?.cost, "0,00.00")
                  : "N/A"}
              </div>
            </Space>
          </Col>
        ) : null}

        <Col span={3}>
          {format(new Date(briefInfluencer.createdAt), "dd MMM yyyy")}
        </Col>

        <Col span={3}>
          <Tag
            color={briefInfluencer.mainStatus === "Campaign" ? "green" : "cyan"}
          >
            {briefInfluencer.mainStatus}
          </Tag>
        </Col>
      </Row>

      {/* <Modal
        title={"View Deliverable"}
        open={deliverableModalProps.visible}
        onCancel={() =>
          setDeliverableModalProps({
            visible: false,
            type: "create",
            deliverableId: "",
            briefInfluencerIds: [],
          })
        }
        footer={null}
        width={1000}
        destroyOnClose
      >
        {deliverableModalProps.visible && (
          <>
            <DeliverablePackageCard
              deliverableId={deliverableModalProps?.deliverableId || ""}
              briefId={briefInfluencer.briefId}
              onClose={() =>
                setDeliverableModalProps({
                  visible: false,
                  type: "create",
                  deliverableId: "",
                  briefInfluencerIds: [],
                })
              }
            />
          </>
        )}
      </Modal> */}
    </>
  );
}
